import { type WorkflowRun } from '@mastra/core/storage';
import {
    type WorkflowRunState,
    type WorkflowRunStatus,
    type WorkflowState,
} from '@mastra/core/workflows';
import { type Href, Link, useLocalSearchParams } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import {
    ArrowRightIcon,
    BadgeLabel,
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CopyIcon,
    Icon,
    IconButton,
    Picker,
    RotateCcwIcon,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { PageContentContainer } from '../../common/components/GroveContainers';
import { TextError } from '../../common/components/InlineError';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { IS_PRODUCTION } from '../../config/FruitfulEnvironment';
import {
    adminUserAtom,
    allGuidesAtom,
    selectedWorkflowRunAtom,
} from '../../entities/entityAtoms';
import { AdminUserRole } from '../../entities/entityTypes';
import { useGetWorkflowRuns } from '../../llm-service/hooks/useGetWorkflowRuns';
import { useMastraClient } from '../../llm-service/providers/MastraClient';
import { WorkflowId } from '../../llm-service/types/workflowIds';
import { captureException } from '../../logger/Logger';
import { useRerunWorkflow } from '../hooks/useRerunWorkflow';
import {
    extractWorkflowRunInput,
    getBadgeVariantFromWorkflowRun,
    getGuideEmailFromWorkflowRun,
    getMembershipIdFromWorklowRun,
    getPostSessionWorkflowRunTitle,
    getSessionDateFromWorkflowRunState,
    getTimeSinceLastUpdate,
    getTimeSinceWorkflowStarted,
    getWorkflowStatusDisplayText,
    normalizeWorkflowRunStatus,
    WorkflowStatus,
} from '../utils/postSessionWorkflowUtils';
import PostSessionWorkflowRunOutputModal from './PostSessionWorkflowRunOutputModal';
import PromptEditorModal from './PromptEditorModal';
import WorkflowSettingsModal from './WorkflowSettingsModal';

const WorkflowsPage = () => {
    const [selectedWorkflowRun, setSelectedWorkflowRun] =
        useState<WorkflowState | null>(null);
    const setSelectedWorkflowRunAtom = useSetAtom(selectedWorkflowRunAtom);
    const [isWorkflowRunOutputModalOpen, setIsWorkflowRunOutputModalOpen] =
        useState(false);
    const [promptKeyToEdit, setPromptKeyToEdit] = useState<string | null>(null);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [selectedGuideEmail, setSelectedGuideEmail] = useState<string>('');

    const adminUser = useAtomValue(adminUserAtom);
    const allGuides = useAtomValue(allGuidesAtom);
    const canAccessWorkflows = adminUser.authorization.workflows.canUse;
    const toast = useToastController();
    const { mutateAsync: rerunWorkflow, isPending: isRerunning } =
        useRerunWorkflow();

    const mastraClient = useMastraClient();
    const workflow = mastraClient.getWorkflow(
        WorkflowId.POST_SESSION_WORKFLOW_ID,
    );

    if (!canAccessWorkflows) return null;

    const { all_runs: allRunsParam } = useLocalSearchParams<{
        all_runs?: string;
    }>();

    const isAllRunsQueryParamSet = allRunsParam === 'true';
    const canSeeAllRuns = adminUser.role !== AdminUserRole.GUIDE;
    const canActionSettings =
        !IS_PRODUCTION ||
        [
            AdminUserRole.SUPERADMIN,
            AdminUserRole.ENGINEER,
            AdminUserRole.GUIDE_LEAD,
        ].includes(adminUser.role);

    // If the current user is a guide, and not the guide lead, filter by guideId
    const resourceIdFilter =
        canSeeAllRuns && isAllRunsQueryParamSet
            ? undefined
            : (adminUser.guideId ?? undefined);

    /* Each column is its own query; both return their full result set. */
    const {
        data: followUpData,
        error: followUpError,
        isLoading: isLoadingFollowUp,
        refetch: refetchFollowUp,
    } = useGetWorkflowRuns({
        workflowId: WorkflowId.POST_SESSION_WORKFLOW_ID,
        resourceId: resourceIdFilter,
        status: 'needs_review',
    });

    const {
        data: completedData,
        error: completedError,
        isLoading: isLoadingCompleted,
        refetch: refetchCompleted,
    } = useGetWorkflowRuns({
        workflowId: WorkflowId.POST_SESSION_WORKFLOW_ID,
        resourceId: resourceIdFilter,
        status: 'success',
    });

    const isLoading = isLoadingFollowUp || isLoadingCompleted;
    const error = followUpError ?? completedError;

    const refetchWorkflowRuns = useCallback(async () => {
        await Promise.all([refetchFollowUp(), refetchCompleted()]);
    }, [refetchFollowUp, refetchCompleted]);

    const applySelectedWorkflowRun = useCallback(
        (run: WorkflowState) => {
            setSelectedWorkflowRun(run);
            setSelectedWorkflowRunAtom(run);
        },
        [setSelectedWorkflowRunAtom],
    );

    const matchesGuideFilter = (run: WorkflowRun) =>
        !selectedGuideEmail ||
        getGuideEmailFromWorkflowRun(run).toLowerCase() ===
            selectedGuideEmail.toLowerCase();

    /**
     * Both columns arrive pre-filtered by status from the server (and failed
     * runs superseded by a later rerun of the same session are already excluded
     * server-side), so the guide dropdown is the only remaining client filter.
     */
    const suspendedWorkflows = useMemo(
        () => (followUpData?.runs ?? []).filter(matchesGuideFilter),
        [followUpData, selectedGuideEmail],
    );
    const completedWorkflows = useMemo(
        () => (completedData?.runs ?? []).filter(matchesGuideFilter),
        [completedData, selectedGuideEmail],
    );

    const handleSelectedWorkflow = async (run: WorkflowRun) => {
        /**
         * The workflow modal makes assumptions about the state of the workflow run.
         * Prevent invalid workflow runs from opening.
         */
        if (
            ![WorkflowStatus.SUSPENDED, WorkflowStatus.SUCCESS].includes(
                normalizeWorkflowRunStatus(
                    (run.snapshot as WorkflowRunState).status,
                ),
            )
        )
            return;

        try {
            // TODO: Add spinner or loading animation while fetching full run
            const fullWorkflowRun = await workflow.runById(run.runId);
            applySelectedWorkflowRun(fullWorkflowRun);
            setIsWorkflowRunOutputModalOpen(true);
        } catch (err) {
            toast.show(
                'Unable to fetch workflow details',
                MutationErrorToastOptions(err),
            );
            captureException(err);
        }
    };

    const handleRerun = async (run: WorkflowRun) => {
        try {
            await rerunWorkflow({
                workflowId: WorkflowId.POST_SESSION_WORKFLOW_ID,
                runId: run.runId,
                resourceId: run.resourceId as string,
            });

            toast.show('Workflow re-run started!', MutationSuccessToastOptions);

            await refetchWorkflowRuns();
        } catch (e) {
            toast.show(
                'Failed to re-run workflow',
                MutationErrorToastOptions(e),
            );
            captureException(e);
        }
    };

    // Create guide filter options
    const guideFilterOptions = useMemo(() => {
        const options = [
            { label: 'All Guides', value: '' },
            ...allGuides.map((guide) => ({
                label: guide.fullName || guide.email,
                value: guide.email,
            })),
        ];
        return options;
    }, [allGuides]);

    return (
        <>
            <XStack
                width="100%"
                gap={12}
                padding={12}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
            >
                {canSeeAllRuns && (
                    <YStack width={200}>
                        <Picker
                            size="tiny"
                            value={selectedGuideEmail}
                            onChange={setSelectedGuideEmail}
                            options={guideFilterOptions}
                        />
                    </YStack>
                )}

                {canActionSettings && (
                    <Button
                        text="Settings"
                        onPress={() => {
                            setIsSettingsModalOpen(true);
                        }}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.TINY}
                        width="auto"
                        paddingHorizontal={16}
                    />
                )}
            </XStack>

            {isLoading && <Spinner />}
            {!isLoading && error && <TextError message={error.message} />}
            {!isLoading &&
                (suspendedWorkflows.length > 0 ||
                    completedWorkflows.length > 0) && (
                    <XStack
                        flex={1}
                        width="100%"
                        gap={24}
                        padding={24}
                        justifyContent="center"
                    >
                        <PageContentContainer
                            flex={1}
                            overflow="auto"
                            maxWidth={450}
                            gap={12}
                            borderRadius={24}
                            alignItems="flex-start"
                        >
                            <BodyLargeEmphasized>
                                Needs Follow-up
                            </BodyLargeEmphasized>

                            {suspendedWorkflows.map((run) => (
                                <WorkflowCard
                                    key={run.runId}
                                    run={run}
                                    onPress={handleSelectedWorkflow}
                                    onRerun={handleRerun}
                                    isRerunning={isRerunning}
                                />
                            ))}
                        </PageContentContainer>

                        <PageContentContainer
                            flex={1}
                            overflow="auto"
                            maxWidth={450}
                            gap={12}
                            borderRadius={24}
                            alignItems="flex-start"
                        >
                            <BodyLargeEmphasized>Completed</BodyLargeEmphasized>

                            {completedWorkflows.map((run) => (
                                <WorkflowCard
                                    key={run.runId}
                                    run={run}
                                    onPress={handleSelectedWorkflow}
                                />
                            ))}
                        </PageContentContainer>
                    </XStack>
                )}

            {isWorkflowRunOutputModalOpen && selectedWorkflowRun != null && (
                <PostSessionWorkflowRunOutputModal
                    isOpen={isWorkflowRunOutputModalOpen}
                    workflowRun={selectedWorkflowRun}
                    onClose={() => {
                        setIsWorkflowRunOutputModalOpen(false);
                        setSelectedWorkflowRun(null);
                        setSelectedWorkflowRunAtom(null);
                        void refetchWorkflowRuns();
                    }}
                    refetchWorkflowRuns={refetchWorkflowRuns}
                />
            )}

            {isSettingsModalOpen && (
                <WorkflowSettingsModal
                    isOpen={isSettingsModalOpen}
                    onClose={() => {
                        setIsSettingsModalOpen(false);
                    }}
                    onEditPrompt={(promptKey) => {
                        setIsSettingsModalOpen(false);
                        setPromptKeyToEdit(promptKey);
                    }}
                    refetchWorkflowRuns={refetchWorkflowRuns}
                />
            )}

            {promptKeyToEdit != null && (
                <PromptEditorModal
                    isOpen
                    promptKey={promptKeyToEdit}
                    onClose={() => {
                        setPromptKeyToEdit(null);
                    }}
                />
            )}
        </>
    );
};

const WorkflowCard = ({
    run,
    onPress,
    onRerun,
    isRerunning,
}: {
    run: WorkflowRun;
    onPress: (run: WorkflowRun) => void;
    onRerun?: (run: WorkflowRun) => Promise<void>;
    isRerunning?: boolean;
}) => {
    const adminUser = useAtomValue(adminUserAtom);
    const isEngineer = adminUser.role === AdminUserRole.ENGINEER;

    const toast = useToastController();
    const mastraClient = useMastraClient();
    const workflow = mastraClient.getWorkflow(
        WorkflowId.POST_SESSION_WORKFLOW_ID,
    );

    const normalizedStatus = normalizeWorkflowRunStatus(
        (run.snapshot as WorkflowRunState).status,
    );

    const isWorkflowReviewable = [
        WorkflowStatus.SUSPENDED,
        WorkflowStatus.SUCCESS,
    ].includes(normalizedStatus);

    const isWorkflowCompleted = normalizedStatus === WorkflowStatus.SUCCESS;
    const isWorkflowFailed = normalizedStatus === WorkflowStatus.FAILED;
    const isWorkflowRunning = normalizedStatus === WorkflowStatus.RUNNING;

    const timeSinceStarted = getTimeSinceWorkflowStarted(run);
    const timeSinceLastUpdate = getTimeSinceLastUpdate(run);
    const guideEmailAddress = getGuideEmailFromWorkflowRun(run);
    const workflowRunInput = extractWorkflowRunInput(run);

    const handleCopyWorkflowOutput = async () => {
        try {
            const fullWorkflowRun = await workflow.runById(run.runId);
            const workflowOutputJson = JSON.stringify(
                fullWorkflowRun.steps,
                null,
                2,
            );
            await navigator.clipboard.writeText(workflowOutputJson);
            toast.show(
                'Workflow output copied to clipboard!',
                MutationSuccessToastOptions,
            );
        } catch (err) {
            captureException(err);
            toast.show(
                'Unable to fetch workflow details',
                MutationErrorToastOptions(err),
            );
        }
    };

    return (
        <YStack
            width="100%"
            backgroundColor="$gray200"
            gap={32}
            padding={24}
            borderRadius={24}
            cursor="pointer"
            onPress={() => {
                onPress(run);
            }}
        >
            <XStack justifyContent="space-between">
                <YStack gap={4}>
                    <WorkflowTitleWithLink run={run} />
                    <BodySmallRegular color="$gray1000">
                        {getSessionDateFromWorkflowRunState(workflowRunInput)}
                    </BodySmallRegular>
                </YStack>

                {isWorkflowReviewable && (
                    <Icon
                        icon={ArrowRightIcon}
                        color="$gray1200"
                        size={16}
                        margin={10}
                    />
                )}
            </XStack>

            {isEngineer && (
                <XStack alignItems="center" justifyContent="space-between">
                    <YStack gap={4}>
                        {timeSinceStarted != null && (
                            <BodySmallRegular color="$gray800">
                                Started: {timeSinceStarted}
                            </BodySmallRegular>
                        )}
                        {timeSinceLastUpdate != null && (
                            <BodySmallRegular color="$gray800">
                                Last update: {timeSinceLastUpdate}
                            </BodySmallRegular>
                        )}
                        <BodySmallRegular color="$gray800">
                            Guide: {guideEmailAddress}
                        </BodySmallRegular>
                    </YStack>

                    <IconButton
                        icon={CopyIcon}
                        variant={ButtonVariant.SECONDARY}
                        onPress={(e) => {
                            e?.stopPropagation();
                            void handleCopyWorkflowOutput();
                        }}
                    />
                </XStack>
            )}

            {!isWorkflowCompleted && (
                <XStack justifyContent="space-between" alignItems="center">
                    <WorkflowCardBadge
                        status={(run.snapshot as WorkflowRunState).status}
                    />
                    {isWorkflowFailed && (
                        <IconButton
                            icon={RotateCcwIcon}
                            variant={ButtonVariant.SECONDARY}
                            disabled={isRerunning}
                            onPress={(e) => {
                                e?.stopPropagation();
                                void onRerun?.(run);
                            }}
                        />
                    )}
                    {isWorkflowRunning && (
                        <BodySmallRegular color="$gray1000" fontStyle="italic">
                            This may take a few minutes
                        </BodySmallRegular>
                    )}
                </XStack>
            )}
        </YStack>
    );
};

const WorkflowTitleWithLink = ({ run }: { run: WorkflowRun }) => {
    const membershipId = getMembershipIdFromWorklowRun(run);
    const workflowRunInput = extractWorkflowRunInput(run);

    if (membershipId === null)
        return (
            <BodyMediumRegular>
                {getPostSessionWorkflowRunTitle(workflowRunInput)}
            </BodyMediumRegular>
        );

    return (
        <Link
            href={`/membership/${membershipId}` as Href}
            onPress={(e) => {
                e.stopPropagation();
            }}
        >
            <BodyMediumRegular hoverStyle={{ textDecorationLine: 'underline' }}>
                {getPostSessionWorkflowRunTitle(workflowRunInput)}
            </BodyMediumRegular>
        </Link>
    );
};

const WorkflowCardBadge = ({ status }: { status: WorkflowRunStatus }) => {
    const normalizedStatus = normalizeWorkflowRunStatus(status);
    const text = getWorkflowStatusDisplayText(normalizedStatus);
    const variant = getBadgeVariantFromWorkflowRun(status);

    if (normalizedStatus === WorkflowStatus.SUCCESS) return;

    return <BadgeLabel text={text} variant={variant} size="regular" />;
};

export default WorkflowsPage;
